import { cn } from '@/lib/utils';

export const BRAND_NAME = 'Swachh Bharat AI';

export type BrandTone = 'color' | 'white';

const ASSET_BASE = `${import.meta.env.BASE_URL}brand/`;

const ASSETS = {
  emblem: { src: `${ASSET_BASE}swachh-bharat-ai-emblem.png`, width: 165, height: 192 },
  wordmark: {
    color: { src: `${ASSET_BASE}swachh-bharat-ai-wordmark-color.png`, width: 390, height: 160 },
    white: { src: `${ASSET_BASE}swachh-bharat-ai-wordmark-white.png`, width: 390, height: 160 },
  },
  horizontal: {
    color: { src: `${ASSET_BASE}swachh-bharat-ai-logo-horizontal-color.png`, width: 960, height: 482 },
    white: { src: `${ASSET_BASE}swachh-bharat-ai-logo-horizontal-white.png`, width: 960, height: 482 },
  },
} as const;

/** India-map emblem only — for compact placements. Decorative when `label` is omitted. */
export function BrandEmblem({ className, label }: { className?: string; label?: string }) {
  const { src, width, height } = ASSETS.emblem;
  return (
    <img
      src={src}
      width={width}
      height={height}
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      decoding="async"
      draggable={false}
      className={cn('h-10 w-auto select-none', className)}
    />
  );
}

/** "Swachh Bharat AI" wordmark only. Decorative when `label` is omitted. */
export function BrandWordmark({ tone = 'color', className, label }: { tone?: BrandTone; className?: string; label?: string }) {
  const { src, width, height } = ASSETS.wordmark[tone];
  return (
    <img
      src={src}
      width={width}
      height={height}
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      decoding="async"
      draggable={false}
      className={cn('h-8 w-auto select-none', className)}
    />
  );
}

const LOCKUP_SIZES = {
  sm: { emblem: 'h-9', wordmark: 'h-[1.875rem]', gap: 'gap-2' },
  md: { emblem: 'h-10 md:h-12', wordmark: 'h-8 md:h-10', gap: 'gap-2.5' },
  lg: { emblem: 'h-14', wordmark: 'h-12', gap: 'gap-3' },
} as const;

/**
 * Compact lockup: emblem + "Swachh Bharat AI" wordmark (no Hindi line or
 * tagline, which become illegible at navigation sizes).
 */
export function BrandLockup({
  tone = 'color',
  size = 'md',
  className,
}: {
  tone?: BrandTone;
  size?: keyof typeof LOCKUP_SIZES;
  className?: string;
}) {
  const s = LOCKUP_SIZES[size];
  return (
    <span className={cn('inline-flex items-center', s.gap, className)} role="img" aria-label={BRAND_NAME}>
      <BrandEmblem className={s.emblem} />
      <BrandWordmark tone={tone} className={s.wordmark} />
    </span>
  );
}

/**
 * Full horizontal logo (emblem, wordmark, स्वच्छ भारत and tagline). Use only
 * where it renders at least ~300px wide so the tagline stays legible.
 */
export function BrandLogo({
  tone = 'color',
  className,
  priority = false,
}: {
  tone?: BrandTone;
  className?: string;
  priority?: boolean;
}) {
  const { src, width, height } = ASSETS.horizontal[tone];
  return (
    <img
      src={src}
      width={width}
      height={height}
      alt={BRAND_NAME}
      decoding="async"
      loading={priority ? 'eager' : 'lazy'}
      draggable={false}
      className={cn('h-auto w-[300px] max-w-full select-none', className)}
    />
  );
}
